// ── La campana ───────────────────────────────────────────────────────────────
//
// Se genera acá, con Web Audio. No hay archivo, y esa es la razón: la pregunta
// de la licencia deja de existir en vez de contestarse. Nada se descarga, nada
// se empaqueta, nada que auditar — y el tono, el largo y el volumen se ajustan
// cambiando números.
//
// Qué la hace sonar a campana y no a pitido:
//   · Dos parciales, no uno. La fundamental más una quinta justa arriba
//     (relación 3:2) da el timbre metálico; una sola sinusoide suena a alarma
//     de horno.
//   · Caída exponencial, no corte. Un corte seco produce un chasquido audible
//     al final; la exponencial se apaga como se apaga un metal golpeado.
//   · Ataque de 8 ms en vez de arrancar en el volumen final. Sin él, el salto
//     de cero produce un clic en el arranque.
//
// Discreta a propósito: 0.09 de ganancia máxima y 900 ms de cola. Está pensada
// para enterarte sin estar mirando, no para sobresaltarte.

/** Frecuencia base. Un si♭5 — alto para que se distinga de fondo, sin chillar. */
const FUNDAMENTAL = 932;
/** Quinta justa: la relación 3:2 es la que suena "campana" en vez de "tono". */
const RELACION_PARCIAL = 1.5;
const DURACION_S = 0.9;
const GANANCIA_MAX = 0.09;
const ATAQUE_S = 0.008;

let contexto: AudioContext | null = null;

/**
 * El contexto de audio, creado a la primera y reusado.
 *
 * Los navegadores no dejan sonar hasta que hubo un gesto de la persona; si el
 * contexto quedó suspendido se reanuda acá. Se ignora el fallo a propósito: que
 * no suene una campana no puede romper la pantalla.
 */
function obtenerContexto(): AudioContext | null {
  try {
    contexto ??= new AudioContext();
    if (contexto.state === 'suspended') void contexto.resume();
    return contexto;
  } catch {
    return null;
  }
}

/** Un parcial: oscilador + su propia envolvente. */
function parcial(ctx: AudioContext, hz: number, ganancia: number, inicio: number): void {
  const osc = ctx.createOscillator();
  const vol = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(hz, inicio);

  vol.gain.setValueAtTime(0.0001, inicio);
  vol.gain.linearRampToValueAtTime(ganancia, inicio + ATAQUE_S);
  // A un valor chico y no a cero: exponentialRamp no acepta 0, y llegar a
  // 0.0001 es inaudible.
  vol.gain.exponentialRampToValueAtTime(0.0001, inicio + DURACION_S);

  osc.connect(vol).connect(ctx.destination);
  osc.start(inicio);
  osc.stop(inicio + DURACION_S + 0.05);
}

/**
 * Suena una vez. No hace nada si el navegador no lo permite todavía.
 *
 * No devuelve error ni promesa: quien la llama no tiene nada que hacer con un
 * fallo, y encadenar un `.catch()` en cada llamada solo agrega ruido.
 */
export function sonarCampana(): void {
  const ctx = obtenerContexto();
  if (!ctx) return;
  const t = ctx.currentTime;
  parcial(ctx, FUNDAMENTAL, GANANCIA_MAX, t);
  // El parcial agudo entra más bajo y se apaga antes — así se percibe como
  // timbre y no como dos notas.
  parcial(ctx, FUNDAMENTAL * RELACION_PARCIAL, GANANCIA_MAX * 0.45, t);
}
